/**
 * Verdict lines for the flattened severity group headers in
 * GroupedPoliciesDisplay (BPA Linear-editorial redesign).
 *
 * Each group band carries a short editorial verdict beside its count, in the
 * advisor voice: name the move, then (only when the data supports it) one
 * grounded observation. Every factual clause is derived from the rows passed
 * in — nothing is invented. No em-dashes (voice rule); short sentences.
 *
 * Pure function so the copy is pinned by unit tests, mirroring
 * posture-brief.ts.
 */

import type { ComplianceViolation } from '@/types'

export type SeverityGroupKey = 'critical' | 'high' | 'medium' | 'passing'

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'] as const

function countWord(n: number): string {
  return n >= 0 && n <= 9 ? NUMBER_WORDS[n] : String(n)
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * The category (workload) that dominates a set of rows, when at least two
 * rows share it. Drives the "Two are Exchange." clause on the critical band.
 */
function dominantCategory(rows: ComplianceViolation[]): { category: string; count: number } | null {
  const counts = new Map<string, number>()
  for (const row of rows) {
    if (!row.category) continue
    counts.set(row.category, (counts.get(row.category) ?? 0) + 1)
  }
  let best: { category: string; count: number } | null = null
  for (const [category, count] of counts) {
    if (!best || count > best.count) best = { category, count }
  }
  return best && best.count >= 2 ? best : null
}

export interface GroupVerdictContext {
  /** Count of OPEN critical findings in the sibling critical group — lets the
   *  high band avoid deferring to a queue that doesn't exist. */
  openCriticals?: number
}

/**
 * Compose the verdict line for one severity group. Empty groups return an
 * empty string (the band itself is not rendered for them).
 */
export function composeGroupVerdict(
  key: SeverityGroupKey,
  policies: ComplianceViolation[],
  context: GroupVerdictContext = {},
): string {
  const total = policies.length
  if (total === 0) return ''

  if (key === 'passing') return 'In place and holding.'

  const open = policies.filter((p) => p.status === 'Open')
  if (open.length === 0) return 'Nothing open in this tier.'

  if (key === 'critical') {
    const lead = open.length === 1 ? 'Close this first.' : 'Close these first.'
    const dominant = dominantCategory(open)
    if (dominant && open.length > 1) {
      const clause =
        dominant.count === open.length
          ? `All are ${dominant.category}.`
          : `${capitalize(countWord(dominant.count))} are ${dominant.category}.`
      return `${lead} ${clause}`
    }
    return lead
  }

  if (key === 'high') {
    // With no open criticals above, this tier IS the front of the queue.
    const lead = (context.openCriticals ?? 0) > 0 ? 'Work these after the criticals.' : 'Start here.'
    const waived = policies.filter((p) => p.status === 'Waived').length
    if (waived > 0) {
      return `${lead} ${capitalize(countWord(waived))} waived.`
    }
    return lead
  }

  // medium
  return 'Hygiene. Batch these when convenient.'
}
